import { useEffect, useRef, useState } from "react";
import { streamAgent, submitDebate, streamDebateTranscript } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useDemoCode } from "../auth/DemoCodeProvider";
import { getRuntimeConfig } from "../runtime-config";

type Mode = "quick" | "deep";

interface Chosen { agent: string; subquestion: string; why: string; }

interface RoundEntry { round: 1 | 2 | 3; agent: string; text: string; }

export function CollaboratorPanel() {
  const cfg = getRuntimeConfig();
  const { user } = useAuth();
  const { code: demoCode } = useDemoCode();
  const [mode, setMode] = useState<Mode>("quick");
  const [round3, setRound3] = useState<boolean>(cfg.collaborator.round3EnabledByDefault);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => cryptoId());

  const [reasoning, setReasoning] = useState<Chosen[] | null>(null);
  const [agentStatus, setAgentStatus] = useState<Record<string, string>>({});
  const [transcript, setTranscript] = useState<RoundEntry[]>([]);
  const [roundStatus, setRoundStatus] = useState<Record<number, string>>({});
  const [finalText, setFinalText] = useState("");
  const [rateWarning, setRateWarning] = useState<{ remaining: number; limit: number } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [finalText]);

  function reset() {
    setReasoning(null); setAgentStatus({}); setTranscript([]); setRoundStatus({}); setFinalText("");
  }

  async function submitDeep(ctrl: AbortController) {
    if (!user) return;
    // POST /debate returns a debateId immediately; the worker runs the 9
    // turns asynchronously and writes each to DDB. We tail those via the
    // Function URL stream.
    const { debateId } = await submitDebate(input, { idToken: user.idToken, demoCode });
    for await (const ev of streamDebateTranscript(debateId, { idToken: user.idToken, signal: ctrl.signal })) {
      if (ev.type === "turn") {
        if (ev.round === "synth") {
          setFinalText(ev.text);
        } else {
          setTranscript((t) => [...t, { round: ev.round as 1 | 2 | 3, agent: ev.agent, text: ev.text }]);
          setRoundStatus((s) => ({ ...s, [ev.round as 1 | 2 | 3]: "complete" }));
        }
      } else if (ev.type === "status") {
        if (ev.status === "failed" || ev.status === "timeout") {
          setFinalText((x) => (x || "") + `\n⚠ Debate ${ev.status}${ev.errorMessage ? `: ${ev.errorMessage}` : ""}`);
        }
      }
    }
  }

  async function submit() {
    if (!input.trim() || busy || !user) return;
    reset();
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      if (mode === "deep") {
        await submitDeep(ctrl);
        return;
      }
      for await (const ev of streamAgent(
        `/chat/collaborator?mode=${mode}`,
        { sessionId, message: input, mode, round3 },
        { idToken: user.idToken, demoCode, signal: ctrl.signal },
      )) {
        switch (ev.type) {
          case "reasoning":
            setReasoning(ev.chosen as Chosen[]); break;
          case "agent":
            setAgentStatus((s) => ({ ...s, [ev.agent]: ev.status })); break;
          case "round":
            setRoundStatus((s) => ({ ...s, [ev.round]: ev.status })); break;
          case "transcript":
            setTranscript((t) => {
              const last = t[t.length - 1];
              if (last && last.round === ev.round && last.agent === ev.agent) {
                const next = t.slice();
                next[next.length - 1] = { ...last, text: last.text + ev.text };
                return next;
              }
              return [...t, { round: ev.round, agent: ev.agent, text: ev.text }];
            });
            break;
          case "token":
            setFinalText((x) => x + ev.text); break;
          case "final":
            setFinalText(ev.text); break;
          case "error":
            setFinalText((x) => (x || "") + `\n⚠ ${ev.message}`); break;
          case "rate-limit-warning":
            setRateWarning({ remaining: ev.remaining, limit: ev.limit }); break;
        }
      }
    } catch (err) {
      const e = err as Error & { kind?: string; limit?: number; resetAt?: string };
      if ((err as DOMException).name !== "AbortError") {
        let msg = "\n⚠ Request failed.";
        if (e.kind === "rate-limit") {
          msg = `\n⚠ Daily limit reached (${e.limit ?? "limit"} ${mode}-mode requests). Resets at ${
            e.resetAt ? new Date(e.resetAt).toLocaleString() : "midnight UTC"
          }.`;
        } else if (e.kind === "unauthorized") {
          msg = "\n⚠ Invalid or missing demo code. Open Settings to enter the invite code.";
        } else if (e.kind === "demo-unavailable") {
          msg = "\n⚠ Demo is paused right now — please try again later.";
        }
        setFinalText((x) => (x || "") + msg);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
      setSessionId(cryptoId());
      setInput("");
    }
  }

  return (
    <section className="collab">
      <div className="collab__header">
        <h2>Collaborator</h2>
        <div className="collab__modes">
          <label>
            <input type="radio" name="mode" checked={mode === "quick"} onChange={() => setMode("quick")} disabled={busy} />
            ⚡ Quick
          </label>
          <label>
            <input type="radio" name="mode" checked={mode === "deep"} onChange={() => setMode("deep")} disabled={busy} />
            🧠 Deep Debate
          </label>
          {mode === "deep" && (
            <label className="collab__round3">
              <input type="checkbox" checked={round3} onChange={(e) => setRound3(e.target.checked)} disabled={busy} />
              Enable Round 3 (final rebuttal)
            </label>
          )}
        </div>
      </div>

      {rateWarning && (
        <div className="chat__ratewarn" role="status">
          Heads up — {rateWarning.remaining} of {rateWarning.limit} {mode}-mode requests left today.
        </div>
      )}
      <div className="collab__body">
      {reasoning && (
        <details className="collab__reasoning" open>
          <summary>Collaborator reasoning — {reasoning.length} agent{reasoning.length === 1 ? "" : "s"} chosen</summary>
          <ul>
            {reasoning.map((c, i) => (
              <li key={i}><strong>{c.agent}</strong>: {c.why}<br /><em>sub-question:</em> {c.subquestion}</li>
            ))}
          </ul>
        </details>
      )}

      {mode === "deep" && transcript.length > 0 && (
        <details className="collab__transcript" open>
          <summary>
            Debate transcript
            {[1,2,3].map((r) => roundStatus[r] && (
              <span key={r} className="round-badge">Round {r}: {roundStatus[r]}</span>
            ))}
          </summary>
          {[1,2,3].map((round) => {
            const entries = transcript.filter((t) => t.round === round);
            if (entries.length === 0) return null;
            return (
              <div key={round} className="collab__round">
                <h3>Round {round}</h3>
                {entries.map((e, i) => (
                  <div key={i} className="collab__contribution">
                    <strong>{e.agent}</strong>
                    <pre>{e.text}</pre>
                  </div>
                ))}
              </div>
            );
          })}
        </details>
      )}

      {Object.keys(agentStatus).length > 0 && (
        <div className="collab__agent-status">
          {Object.entries(agentStatus).map(([a, s]) => (
            <span key={a} className={`status status--${s}`}>{a}: {s}</span>
          ))}
        </div>
      )}

      {finalText && (
        <div className="collab__final">
          <h3>Final answer</h3>
          <div className="collab__final-text">{finalText}{busy && <span className="cursor">▍</span>}</div>
        </div>
      )}
      <div ref={bottomRef} />
      </div>

      <form className="collab__input" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === "quick"
            ? "Ask a question — the Collaborator picks which agents to invoke."
            : "Ask a question — all three agents debate, then the Collaborator synthesizes."}
          rows={3}
          disabled={busy}
          onKeyDown={(e) => {
            // Match ChatPanel UX: Enter submits, Shift+Enter inserts a newline.
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
          }}
        />
        {busy
          ? <button type="button" className="btn-cancel" onClick={() => abortRef.current?.abort()}>Stop</button>
          : <button type="submit" className="btn-send" disabled={!input.trim()}>Submit</button>}
      </form>
    </section>
  );
}

function cryptoId(): string {
  return (globalThis.crypto?.randomUUID?.() ?? String(Date.now())).slice(0, 32);
}
